import 'svelte/internal/disclose-version';
import * as $ from 'svelte/internal/client';
import { onMount } from "svelte";
import { mount as mountSvelte, unmount } from "svelte";

import {
	autocompletion,
	closeBrackets,
	closeBracketsKeymap,
	completionKeymap
} from "@codemirror/autocomplete";

import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { javascript, javascriptLanguage } from "@codemirror/lang-javascript";
import { markdown, markdownLanguage } from "@codemirror/lang-markdown";

import {
	bracketMatching,
	defaultHighlightStyle,
	foldGutter,
	foldKeymap,
	indentOnInput,
	syntaxHighlighting
} from "@codemirror/language";

import { lintKeymap } from "@codemirror/lint";
import { highlightSelectionMatches, searchKeymap } from "@codemirror/search";
import { EditorState } from "@codemirror/state";

import {
	crosshairCursor,
	drawSelection,
	dropCursor,
	highlightActiveLine,
	highlightActiveLineGutter,
	highlightSpecialChars,
	keymap,
	lineNumbers,
	rectangularSelection
} from "@codemirror/view";

import { mount as mountSlexKit, parseSlexSource } from "../../../engine/index";
import { bindPropStore } from "../bindProps";
import { stringifySource, text } from "../helpers";
import Button from "../input/Button.svelte";
import Select from "../input/Select.svelte";
import Tabs from "../input/Tabs.svelte";
import CodeMirror from "./CodeMirror.svelte";
import PlaygroundMarkdown from "./PlaygroundMarkdown.svelte";

var root = $.from_html(`<div class="slex-playground-code-pane"><div class="slex-playground-code"><!></div></div>`);
var root_1 = $.from_html(`<div class="slex-playground-preview-pane"><div class="slex-playground-preview"></div></div>`);
var root_2 = $.from_html(`<div><div class="slex-playground-live-code"><div class="slex-playground-code"><!></div></div> <div role="separator" tabindex="0"></div> <div class="slex-playground-live-preview"></div></div>`);
var root_3 = $.from_html(`<section><h2 class="slex-playground-title slex-sr-only"> </h2> <div class="slex-playground-chrome"><div class="slex-playground-tabs" role="group" aria-label="Playground view"><!> <div class="slex-playground-source-picker"><!></div></div> <div class="slex-playground-actions"><!> <!> <!></div></div> <!></section>`);

export default function Playground($$anchor, $$props) {
	$.push($$props, true);

	let p = $.state($.proxy({}));
	let mode = $.state("render");
	let source = $.state("");
	let previewSource = $.state("");
	let sourceType = $.state("markdown");
	let lastSource = "";
	let editorView = $.state(null);
	let playgroundNode = $.state(null);
	let previewNode = $.state(null);
	let previewOverflow = $.state(false);
	let previewAnchorFixed = $.state(false);
	let previewAnchorOffset = $.state(0);
	let compact = $.state(false);
	let splitPercent = $.state(48);
	let configuredMode = "";
	let configuredSplit = "";
	let configuredSourceType = "";
	let dragging = $.state(false);
	let currentTheme = $.state("light");
	let splitSurface = null;
	let activeDragPointerId = null;
	let previewOverflowFrame = 0;
	const themeStorageKey = "slexkit:theme";

	const modeItems = [
		{ id: "render", label: "Render", icon: "eye" },
		{ id: "live", label: "Live", icon: "square-split-horizontal" },
		{ id: "code", label: "Code", icon: "code" }
	];

	const sourceTypeItems = [
		{ label: "Markdown", value: "markdown" },
		{ label: "SLEX", value: "slex" }
	];

	const basicEditorSetup = [
		lineNumbers(),
		highlightActiveLineGutter(),
		highlightSpecialChars(),
		history(),
		foldGutter(),
		drawSelection(),
		dropCursor(),
		EditorState.allowMultipleSelections.of(true),
		indentOnInput(),
		syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
		bracketMatching(),
		closeBrackets(),
		autocompletion(),
		rectangularSelection(),
		crosshairCursor(),
		highlightActiveLine(),
		highlightSelectionMatches(),
		keymap.of([
			...closeBracketsKeymap,
			...defaultKeymap,
			...searchKeymap,
			...historyKeymap,
			...foldKeymap,
			...completionKeymap,
			...lintKeymap
		])
	];

	const slexLanguage = javascript();
	const javascriptEditorExtensions = [basicEditorSetup, slexLanguage];

	const markdownEditorExtensions = [
		basicEditorSetup,
		markdown({
			base: markdownLanguage,
			codeLanguages: (info) => {
				const language = info.trim().toLowerCase().split(/\s+/, 1)[0];

				return isSlexLanguage(language) ? javascriptLanguage : null;
			},
			defaultCodeLanguage: slexLanguage
		})
	];

	const editorExtensions = $.derived(() => $.get(sourceType) === "slex" ? javascriptEditorExtensions : markdownEditorExtensions);

	$.user_effect(() => bindPropStore($$props.props, (next) => {
		$.set(p, next, true);

		const nextSource = stringifySource(next.source);
		const sourceChanged = nextSource !== lastSource;

		if (sourceChanged) {
			$.set(source, nextSource, true);
			$.set(previewSource, nextSource, true);
			lastSource = nextSource;
			resetPreviewAnchor();
		}

		const nextSourceType = text(next.sourceType ?? next.type, "");

		if (nextSourceType !== configuredSourceType || !nextSourceType && sourceChanged) {
			configuredSourceType = nextSourceType;
			$.set(sourceType, normalizeSourceKind(nextSourceType, nextSource), true);
		}

		const nextMode = text(next.mode ?? next.webMode, "");

		if (nextMode !== configuredMode) {
			configuredMode = nextMode;

			if (nextMode) $.set(mode, normalizeMode(nextMode), true);
		}

		const nextSplit = text(next.splitPercent ?? next.split, "");

		if (nextSplit !== configuredSplit) {
			configuredSplit = nextSplit;

			const parsed = Number(nextSplit);

			if (Number.isFinite(parsed)) {
				$.set(splitPercent, Math.min(72, Math.max(18, parsed)), true);
			}
		}
	}));

	$.user_effect(() => {
		const next = $.get(source);

		const timer = window.setTimeout(
			() => {
				$.set(previewSource, next, true);
			},
			300
		);

		return () => window.clearTimeout(timer);
	});

	$.user_effect(() => {
		if ($.get(mode) !== "live" || !$.get(editorView) || !$.get(previewNode)) return;

		const codeScroller = $.get(editorView).scrollDOM;
		const previewScroller = $.get(previewNode);
		let syncing = false;

		const sync = (sourceNode, targetNode) => {
			if (syncing) return;

			const sourceMax = Math.max(0, sourceNode.scrollHeight - sourceNode.clientHeight);
			const targetMax = Math.max(0, targetNode.scrollHeight - targetNode.clientHeight);

			if (!sourceMax || !targetMax) return;

			syncing = true;
			targetNode.scrollTop = sourceNode.scrollTop / sourceMax * targetMax;

			window.requestAnimationFrame(() => {
				syncing = false;
			});
		};

		const syncPreview = () => sync(codeScroller, previewScroller);
		const syncCode = () => sync(previewScroller, codeScroller);

		codeScroller.addEventListener("scroll", syncPreview, { passive: true });
		previewScroller.addEventListener("scroll", syncCode, { passive: true });

		return () => {
			codeScroller.removeEventListener("scroll", syncPreview);
			previewScroller.removeEventListener("scroll", syncCode);
		};
	});

	$.user_effect(() => {
		const node = $.get(previewNode);

		if (!node) {
			$.set(previewOverflow, false);

			return;
		}

		schedulePreviewOverflowMeasure();

		if (typeof ResizeObserver === "undefined") return;

		const observer = new ResizeObserver(schedulePreviewOverflowMeasure);

		observer.observe(node);

		const container = previewScrollContainer();

		if (container && container !== node) observer.observe(container);

		const mutationObserver = new MutationObserver(schedulePreviewOverflowMeasure);

		mutationObserver.observe(node, { childList: true, subtree: true });

		return () => {
			observer.disconnect();
			mutationObserver.disconnect();

			if (previewOverflowFrame) {
				window.cancelAnimationFrame(previewOverflowFrame);
				previewOverflowFrame = 0;
			}
		};
	});

	onMount(() => {
		$.set(currentTheme, document.documentElement.classList.contains("dark") ? "dark" : "light", true);

		const media = window.matchMedia("(max-width: 640px)");

		const updateCompact = () => {
			const width = $.get(playgroundNode)?.getBoundingClientRect().width ?? 0;

			$.set(compact, media.matches || width > 0 && width <= 640, true);
		};

		const cancelDrag = () => {
			endDrag();
		};

		const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateCompact);

		updateCompact();
		media.addEventListener("change", updateCompact);
		window.addEventListener("resize", updateCompact);

		if ($.get(playgroundNode)) observer?.observe($.get(playgroundNode));

		window.addEventListener("pointerup", cancelDrag);
		window.addEventListener("pointercancel", cancelDrag);
		window.addEventListener("blur", cancelDrag);
		document.addEventListener("visibilitychange", cancelDrag);

		return () => {
			media.removeEventListener("change", updateCompact);
			window.removeEventListener("resize", updateCompact);
			observer?.disconnect();
			window.removeEventListener("pointerup", cancelDrag);
			window.removeEventListener("pointercancel", cancelDrag);
			window.removeEventListener("blur", cancelDrag);
			document.removeEventListener("visibilitychange", cancelDrag);
		};
	});

	function themeToggleEnabled() {
		const value = $.get(p).themeToggle ?? $.get(p).showThemeToggle ?? $.get(p).enableThemeToggle;

		return value === true || value === "true" || value === 1 || value === "1";
	}

	function normalizeMode(value) {
		const raw = value.trim().toLowerCase();

		if (raw === "code" || raw === "script" || raw === "editor") return "code";
		if (raw === "render" || raw === "preview") return "render";
		if (raw === "live" || raw === "split" || raw === "both") return "live";

		return "render";
	}

	function normalizePreviewAlign(value) {
		const raw = value.trim().toLowerCase();

		return raw === "start" || raw === "top" ? "start" : "center";
	}

	function resolvedPreviewAlign() {
		const explicit = text($.get(p).previewAlign ?? $.get(p).alignPreview ?? $.get(p).previewPlacement, "");

		if (explicit) return normalizePreviewAlign(explicit);

		return $.get(sourceType) === "markdown" ? "start" : "center";
	}

	function sourceSelectProps() {
		return {
			subscribe(run) {
				run({
					value: $.get(sourceType),
					variant: "toolbar",
					options: sourceTypeItems,
					"aria-label": text($.get(p).sourceTypeLabel, "Source type")
				});

				return () => {};
			}
		};
	}

	function viewTabsProps() {
		return {
			subscribe(run) {
				run({
					value: $.get(mode),
					tabs: modeItems.map((item) => ({
						value: item.id,
						label: item.label,
						icon: item.icon,
						iconOnly: true,
						title: item.label
					}))
				});

				return () => {};
			}
		};
	}

	function viewTabsCtx() {
		return {
			...$$props.ctx,
			emit(eventName, data) {
				if (eventName === "change") {
					const nextMode = normalizeMode(text(data));

					if (nextMode !== $.get(mode)) resetPreviewAnchor();

					$.set(mode, nextMode, true);

					return;
				}

				$$props.ctx.emit(eventName, data);
			}
		};
	}

	function actionButtonProps(action) {
		return {
			subscribe(run) {
				run(action === "theme"
					? {
						variant: "ghost",
						icon: $.get(currentTheme) === "dark" ? "sun" : "moon",
						iconOnly: true,
						pressed: $.get(currentTheme) === "dark",
						title: text($.get(p).themeLabel ?? $.get(p).themeToggleLabel, "Toggle theme"),
						"aria-label": text($.get(p).themeLabel ?? $.get(p).themeToggleLabel, "Toggle theme")
					}
					: action === "web"
						? {
							variant: "ghost",
							icon: "arrow-square-out",
							iconOnly: true,
							title: text($.get(p).openWebLabel, "Open in playground"),
							"aria-label": text($.get(p).openWebLabel, "Open in playground")
						}
						: {
							variant: "ghost",
							icon: "copy",
							iconOnly: true,
							title: text($.get(p).copyLabel, "Copy source"),
							"aria-label": text($.get(p).copyLabel, "Copy source")
						});

				return () => {};
			}
		};
	}

	function actionButtonCtx(action) {
		return {
			...$$props.ctx,
			emit(eventName, data) {
				if (eventName === "click") {
					if (action === "theme") toggleTheme(); else if (action === "web") openWeb(); else void copySource();

					return;
				}

				$$props.ctx.emit(eventName, data);
			}
		};
	}

	function toggleTheme() {
		const next = document.documentElement.classList.contains("dark") ? "light" : "dark";

		$.set(currentTheme, next, true);
		document.documentElement.classList.toggle("dark", next === "dark");
		document.documentElement.classList.toggle("light", next === "light");
		document.documentElement.dataset.theme = next;

		try {
			window.localStorage?.setItem(themeStorageKey, next);
		} catch {
			// Storage can be unavailable in privacy-restricted contexts.
		}
	}

	function sourceSelectCtx() {
		return {
			...$$props.ctx,
			emit(eventName, data) {
				if (eventName === "change" || eventName === "select") {
					const nextSourceType = normalizeSourceKind(text(data), $.get(source));

					if (nextSourceType !== $.get(sourceType)) resetPreviewAnchor();

					$.set(sourceType, nextSourceType, true);

					return;
				}

				$$props.ctx.emit(eventName, data);
			}
		};
	}

	function openWeb() {
		const base = text($.get(p).webUrl ?? $.get(p).playgroundUrl, "/playground.html");
		const url = new URL(base, window.location.href);

		url.searchParams.set("srcs", $.get(source));
		url.searchParams.set("type", $.get(sourceType));
		url.searchParams.set("mode", $.get(mode));

		const domain = text($.get(p).domain);
		const pluginVersion = text($.get(p).pluginVersion ?? $.get(p).version);

		if (domain) url.searchParams.set("domain", domain);
		if (pluginVersion) url.searchParams.set("pluginVersion", pluginVersion);

		window.open(url.toString(), "_blank", "noopener,noreferrer");
	}

	async function copySource() {
		if (navigator.clipboard?.writeText) {
			try {
				await navigator.clipboard.writeText($.get(source));

				return;
			} catch {
				// Fall back for non-secure origins or denied clipboard permissions.
			}
		}

		const textarea = document.createElement("textarea");

		textarea.value = $.get(source);
		textarea.setAttribute("readonly", "");
		textarea.style.position = "fixed";
		textarea.style.opacity = "0";
		document.body.appendChild(textarea);
		textarea.select();
		document.execCommand("copy");
		textarea.remove();
	}

	function startDrag(event) {
		if (!splitSurface) return;

		event.preventDefault();
		activeDragPointerId = typeof event.pointerId === "number" ? event.pointerId : null;
		$.set(dragging, true);

		if (typeof splitSurface.setPointerCapture !== "function") return;

		try {
			splitSurface.setPointerCapture(event.pointerId);
		} catch {
			// Pointer capture can fail if the browser has already cancelled the pointer.
		}
	}

	function updateDrag(event) {
		if (!$.get(dragging) || !splitSurface) return;

		const rect = splitSurface.getBoundingClientRect();

		const raw = $.get(compact)
			? (event.clientY - rect.top) / rect.height * 100
			: (event.clientX - rect.left) / rect.width * 100;

		$.set(splitPercent, Math.min(72, Math.max(18, raw)), true);
	}

	function endDrag(event) {
		const pointerId = event?.pointerId ?? activeDragPointerId;

		$.set(dragging, false);
		activeDragPointerId = null;

		if (splitSurface && pointerId !== null && typeof splitSurface.hasPointerCapture === "function" && typeof splitSurface.releasePointerCapture === "function" && splitSurface.hasPointerCapture(pointerId)) {
			splitSurface.releasePointerCapture(pointerId);
		}
	}

	function splitStyle() {
		return $.get(compact)
			? `grid-template-rows:${$.get(splitPercent)}% 8px minmax(0, 1fr);`
			: `grid-template-columns:${$.get(splitPercent)}% 8px minmax(0, 1fr);`;
	}

	function previewScrollContainer() {
		if (!$.get(previewNode)) return null;

		return $.get(mode) === "render" ? $.get(previewNode).parentElement : $.get(previewNode);
	}

	function resetPreviewAnchor() {
		$.set(previewAnchorFixed, false);
		$.set(previewAnchorOffset, 0);
	}

	function capturePreviewAnchor() {
		if (!$.get(previewNode) || $.get(previewOverflow) || resolvedPreviewAlign() !== "center") return;

		const container = previewScrollContainer();

		if (!container) return;

		const target = $.get(mode) === "render"
			? $.get(previewNode)
			: $.get(previewNode).firstElementChild;

		if (!target) return;

		const containerRect = container.getBoundingClientRect();
		const targetRect = target.getBoundingClientRect();
		const styles = window.getComputedStyle(container);
		const paddingTop = Number.parseFloat(styles.paddingTop || "0");

		$.set(previewAnchorOffset, Math.max(0, targetRect.top - containerRect.top - paddingTop + container.scrollTop), true);
		$.set(previewAnchorFixed, true);
	}

	function measurePreviewOverflow() {
		if (!$.get(previewNode)) {
			$.set(previewOverflow, false);

			return;
		}

		const container = previewScrollContainer();

		if (!container) {
			$.set(previewOverflow, false);

			return;
		}

		const content = $.get(previewNode).firstElementChild instanceof HTMLElement
			? $.get(previewNode).firstElementChild
			: $.get(previewNode);

		const styles = window.getComputedStyle(container);
		const availableHeight = Math.max(0, container.clientHeight - Number.parseFloat(styles.paddingTop || "0") - Number.parseFloat(styles.paddingBottom || "0"));
		const availableWidth = Math.max(0, container.clientWidth - Number.parseFloat(styles.paddingLeft || "0") - Number.parseFloat(styles.paddingRight || "0"));

		$.set(previewOverflow, content.scrollHeight > availableHeight + 1 || content.scrollWidth > availableWidth + 1, true);
	}

	function schedulePreviewOverflowMeasure() {
		if (previewOverflowFrame) return;

		previewOverflowFrame = window.requestAnimationFrame(() => {
			previewOverflowFrame = 0;
			measurePreviewOverflow();
		});
	}

	function isSlexLanguage(language) {
		return language === "slex";
	}

	function resolveSourceKind(value) {
		return $.get(sourceType);
	}

	function normalizeSourceKind(value, valueSource = $.get(source)) {
		const raw = value.trim().toLowerCase();

		if (raw === "markdown" || raw === "site-markdown" || raw === "md") return "markdown";
		if (isSlexLanguage(raw)) return "slex";

		return looksLikeSlexSource(valueSource) ? "slex" : "markdown";
	}

	function looksLikeSlexSource(value) {
		const raw = String(value ?? "").trim();

		if (!raw || (/^(```|~~~)/).test(raw)) return false;
		if (!(/^(?:export\s+default\s+)?\(?\s*\{/).test(raw)) return false;

		return (/["']?(slex|namespace|layout|g)["']?\s*:/).test(raw);
	}

	function parseSlexDocuments(value) {
		const raw = String(value ?? "").trim();

		if (!raw) return { ok: true, value: [] };

		const wrapped = raw.startsWith("[") ? raw : `[${raw}]`;
		const parsed = parseSlexSource(wrapped);

		if (!parsed.ok) return parsed;

		const valueList = Array.isArray(parsed.value) ? parsed.value : [parsed.value];

		return {
			ok: true,
			value: valueList.filter((item) => !!item && typeof item === "object")
		};
	}

	function analyze(value) {
		const blocks = Array.from(value.matchAll(/(```|~~~)slex\s*\n([\s\S]*?)\n\1/g), (match, index) => {
			const code = match[2];
			const startIndex = (match.index ?? 0) + match[0].indexOf(code);

			return {
				code,
				index: index + 1,
				startLine: value.slice(0, startIndex).split("\n").length
			};
		});

		const candidates = blocks.length
			? blocks
			: looksLikeSlexSource(value) ? [{ code: value, index: 1, startLine: 1 }] : [];

		for (const block of candidates) {
			if (!block.code.trim()) continue;

			const parsed = parseSlexDocuments(block.code);

			if (!parsed.ok) {
				const diagnostic = parsed.diagnostic;

				return {
					ok: false,
					block: block.index,
					message: diagnostic.message,
					editorLine: block.startLine + diagnostic.line - 1,
					column: diagnostic.column,
					detail: diagnostic.detail ?? "",
					excerpt: diagnostic.excerpt
				};
			}
		}

		return { ok: true };
	}

	function preview(node) {
		let cleanup;
		let markdownApp;
		let renderToken = 0;

		const renderNow = () => {
			cleanup?.();
			cleanup = undefined;

			if (markdownApp) {
				void unmount(markdownApp);
				markdownApp = undefined;
			}

			node.replaceChildren();

			const diagnostic = analyze($.get(previewSource));

			if (!diagnostic.ok) {
				node.appendChild(syntaxErrorNode(diagnostic));
				schedulePreviewOverflowMeasure();

				return;
			}

			if (resolveSourceKind($.get(previewSource)) === "slex") {
				const host = previewInnerNode();

				node.appendChild(host);

				const parsed = parseSlexDocuments($.get(previewSource));

				if (!parsed.ok) {
					node.replaceChildren();

					node.appendChild(syntaxErrorNode({
						ok: false,
						block: 1,
						message: parsed.diagnostic.message,
						editorLine: parsed.diagnostic.line,
						column: parsed.diagnostic.column,
						detail: parsed.diagnostic.detail ?? "",
						excerpt: parsed.diagnostic.excerpt
					}));

					schedulePreviewOverflowMeasure();

					return;
				}

				const cleanups = parsed.value.map((script) => {
					const block = document.createElement("div");

					block.className = "slex-playground-document";
					host.appendChild(block);

					return mountSlexKit(script, block);
				});

				cleanup = () => {
					for (const dispose of cleanups) dispose();
				};
			} else {
				const host = previewInnerNode();

				node.appendChild(host);

				markdownApp = mountSvelte(PlaygroundMarkdown, {
					target: host,
					props: {
						content: $.get(previewSource),
						domain: text($.get(p).domain, "playground")
					}
				});
			}

			schedulePreviewOverflowMeasure();
		};

		const render = () => {
			const token = ++renderToken;

			queueMicrotask(() => {
				if (token !== renderToken) return;

				renderNow();
			});
		};

		render();

		return {
			update: render,
			destroy() {
				renderToken += 1;
				cleanup?.();

				if (markdownApp) void unmount(markdownApp);

				node.replaceChildren();
			}
		};
	}

	function syntaxErrorNode(diagnostic) {
		const wrapper = previewInnerNode();
		const panel = document.createElement("div");

		panel.className = "slex-standalone-playground-error";
		panel.setAttribute("role", "alert");
		panel.append(element("div", "slex-standalone-playground-error-title", "SlexKit syntax error"), element("div", "slex-standalone-playground-error-message", diagnostic.message), element("div", "slex-standalone-playground-error-location", `Block ${diagnostic.block}, editor line ${diagnostic.editorLine}, column ${diagnostic.column}`));

		if (diagnostic.detail) {
			panel.appendChild(element("div", "slex-standalone-playground-error-detail", diagnostic.detail));
		}

		panel.appendChild(element("pre", "slex-standalone-playground-error-excerpt", diagnostic.excerpt ?? ""));
		wrapper.appendChild(panel);

		return wrapper;
	}

	function element(tag, className, content) {
		const node = document.createElement(tag);

		node.className = className;
		node.textContent = content;

		return node;
	}

	function previewInnerNode() {
		const host = document.createElement("div");

		host.className = "slex-standalone-playground-preview-inner";

		const maxWidth = text($.get(p).previewMaxWidth, "");

		if (maxWidth) host.style.maxWidth = maxWidth;

		return host;
	}

	var section = root_3();
	var h2 = $.child(section);
	var text_1 = $.child(h2, true);

	$.reset(h2);

	var div = $.sibling(h2, 2);
	var div_1 = $.child(div);
	var node_1 = $.child(div_1);

	{
		let $0 = $.derived(viewTabsCtx);
		let $1 = $.derived(viewTabsProps);

		Tabs(node_1, {
			componentName: 'tabs',
			get ctx() {
				return $.get($0);
			},

			get props() {
				return $.get($1);
			}
		});
	}

	var div_2 = $.sibling(node_1, 2);
	var node_2 = $.child(div_2);

	{
		let $0 = $.derived(sourceSelectCtx);
		let $1 = $.derived(sourceSelectProps);

		Select(node_2, {
			componentName: 'select',
			get ctx() {
				return $.get($0);
			},

			get props() {
				return $.get($1);
			}
		});
	}

	$.reset(div_2);
	$.reset(div_1);

	var div_3 = $.sibling(div_1, 2);
	var node_3 = $.child(div_3);

	{
		var consequent = ($$anchor) => {
			{
				let $0 = $.derived(() => actionButtonCtx("theme"));
				let $1 = $.derived(() => actionButtonProps("theme"));

				Button($$anchor, {
					componentName: 'button',
					get ctx() {
						return $.get($0);
					},

					get props() {
						return $.get($1);
					}
				});
			}
		};

		var d = $.derived(() => themeToggleEnabled());

		$.if(node_3, ($$render) => {
			if ($.get(d)) $$render(consequent);
		});
	}

	var node_4 = $.sibling(node_3, 2);

	{
		let $0 = $.derived(() => actionButtonCtx("web"));
		let $1 = $.derived(() => actionButtonProps("web"));

		Button(node_4, {
			componentName: 'button',
			get ctx() {
				return $.get($0);
			},

			get props() {
				return $.get($1);
			}
		});
	}

	var node_5 = $.sibling(node_4, 2);

	{
		let $0 = $.derived(() => actionButtonCtx("copy"));
		let $1 = $.derived(() => actionButtonProps("copy"));

		Button(node_5, {
			componentName: 'button',
			get ctx() {
				return $.get($0);
			},

			get props() {
				return $.get($1);
			}
		});
	}

	$.reset(div_3);
	$.reset(div);

	var node_6 = $.sibling(div, 2);

	{
		var consequent_1 = ($$anchor) => {
			var div_4 = root();
			var div_5 = $.child(div_4);
			var node_7 = $.child(div_5);

			CodeMirror(node_7, {
				class: 'slex-playground-editor',
				get doc() {
					return $.get(source);
				},

				get extensions() {
					return $.get(editorExtensions);
				},
				onChange: (value) => $.set(source, value, true),
				onEditorView: (view) => $.set(editorView, view, true)
			});

			$.reset(div_5);
			$.reset(div_4);
			$.append($$anchor, div_4);
		};

		var consequent_2 = ($$anchor) => {
			var div_6 = root_1();
			var div_7 = $.child(div_6);

			$.bind_this(div_7, ($$value) => $.set(previewNode, $$value), () => $.get(previewNode));
			$.action(div_7, ($$node, $$action_arg) => preview?.($$node, $$action_arg), () => `${$.get(sourceType)}:${$.get(previewSource)}`);
			$.reset(div_6);
			$.event('pointerdown', div_6, capturePreviewAnchor, true);
			$.event('keydown', div_6, capturePreviewAnchor, true);
			$.append($$anchor, div_6);
		};

		var alternate = ($$anchor) => {
			var div_8 = root_2();
			var div_9 = $.child(div_8);
			var div_10 = $.child(div_9);
			var node_8 = $.child(div_10);

			CodeMirror(node_8, {
				class: 'slex-playground-editor',
				get doc() {
					return $.get(source);
				},

				get extensions() {
					return $.get(editorExtensions);
				},
				onChange: (value) => $.set(source, value, true),
				onEditorView: (view) => $.set(editorView, view, true)
			});

			$.reset(div_10);
			$.reset(div_9);

			var div_11 = $.sibling(div_9, 2);
			let classes;
			var div_12 = $.sibling(div_11, 2);

			$.bind_this(div_12, ($$value) => $.set(previewNode, $$value), () => $.get(previewNode));
			$.action(div_12, ($$node, $$action_arg) => preview?.($$node, $$action_arg), () => `${$.get(sourceType)}:${$.get(previewSource)}`);
			$.reset(div_8);
			$.bind_this(div_8, ($$value) => splitSurface = $$value, () => splitSurface);

			$.template_effect(
				($0) => {
					$.set_class(div_8, 1, `slex-playground-live-pane ${$.get(compact) ? "vertical" : "horizontal"}`);
					$.set_style(div_8, $0);
					classes = $.set_class(div_11, 1, 'slex-playground-splitter', null, classes, { dragging: $.get(dragging) });
					$.set_attribute(div_11, 'aria-orientation', $.get(compact) ? "horizontal" : "vertical");
				},
				[() => splitStyle()]
			);

			$.delegated('pointermove', div_8, updateDrag);
			$.delegated('pointerup', div_8, endDrag);
			$.event('pointercancel', div_8, endDrag);
			$.event('lostpointercapture', div_8, endDrag);
			$.delegated('pointerdown', div_11, startDrag);
			$.event('pointerdown', div_12, capturePreviewAnchor, true);
			$.event('keydown', div_12, capturePreviewAnchor, true);
			$.append($$anchor, div_8);
		};

		$.if(node_6, ($$render) => {
			if ($.get(mode) === "code") $$render(consequent_1); else if ($.get(mode) === "render") $$render(consequent_2, 1); else $$render(alternate, -1);
		});
	}

	$.reset(section);
	$.bind_this(section, ($$value) => $.set(playgroundNode, $$value), () => $.get(playgroundNode));

	$.template_effect(
		($0, $1, $2, $3) => {
			$.set_class(section, 1, $0);
			$.set_attribute(section, 'data-mode', $.get(mode));
			$.set_attribute(section, 'data-source-type', $.get(sourceType));
			$.set_attribute(section, 'data-preview-align', $1);
			$.set_attribute(section, 'data-preview-overflow', $.get(previewOverflow) ? "true" : "false");
			$.set_attribute(section, 'data-preview-anchor', $.get(previewAnchorFixed) ? "fixed" : "auto");
			$.set_style(section, $2);
			$.set_text(text_1, $3);
		},
		[
			() => `slex-playground slex-playground--workbench${$.get(p).class ? ` ${text($.get(p).class)}` : ""}`,
			() => resolvedPreviewAlign(),
			() => `--slex-playground-min-height:${text($.get(p).previewMinHeight, "16rem")};--slex-preview-anchor-offset:${$.get(previewAnchorOffset)}px;`,
			() => text($.get(p).title, "Playground")
		]
	);

	$.append($$anchor, section);
	$.pop();
}

$.delegate(['pointermove', 'pointerup', 'pointerdown']);